import React, { useContext, useState } from 'react';
import { Modal, Flex, message, Alert } from 'antd';
import DebouncedAdminSelect from '../@shared/admins/DebouncedAdminSelect';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { IAdmin } from '@leedpulse-monorepo/interfaces';
import updateAdmin from '../../../domain/use_cases/admins/updateAdmin';
import getAdminById from '../../../domain/use_cases/admins/getAdminById';
import createAdmin from '../../../domain/use_cases/admins/createAdmin';

interface AdminCopyModalProps {
    open: boolean;
    onCancel: () => void;
    baseAdmin: IAdmin;
    onCopied?: (id: string) => void | Promise<void>;
}

const AdminCopyModal = ({ open, onCancel, baseAdmin, onCopied }: AdminCopyModalProps) => {
    const authContext = useContext(AuthenticationContext);

    const [selectedAdmin, setSelectedAdmin] = useState<IAdmin["id"] | null>(baseAdmin.id);
    const [isLoading, setIsLoading] = useState(false);

    const handleValidate = async () => {
        if (!authContext.authentication) {
            message.error('Vous devez être connecté pour effectuer cette action');
            return;
        }

        if (!selectedAdmin || selectedAdmin === baseAdmin.id) {
            message.error('Veuillez sélectionner une configuration cible différente');
            return;
        }
        // setStatus(ContextStatus.LOADING);

        try {
            setIsLoading(true);
            const targetAdmin = await getAdminById(selectedAdmin);

            // Build copy payload: all properties from source except id
            const payload: Record<string, unknown> = { ...baseAdmin as Record<string, unknown>, id: selectedAdmin || '' };
            payload['snapshot'] = false; // Never copy snapshot value
            // The outbound pricing grid and its enforce flag are per environment: a demo grid
            // must never land in production through a copy, the target keeps its own
            payload['telephony'] = {
                ...baseAdmin.telephony,
                call_outbound_pricing: targetAdmin?.telephony?.call_outbound_pricing ?? null,
                call_outbound_pricing_enforced: targetAdmin?.telephony?.call_outbound_pricing_enforced ?? false,
            };

            if (targetAdmin) await updateAdmin(payload as IAdmin, authContext.authentication);
            else await createAdmin(payload as IAdmin, authContext.authentication);

            message.success(`Configuration copiée avec succès vers ${selectedAdmin}. ${targetAdmin ? '' : 'Un document a été créé.'}`);
            onCancel();
            await onCopied?.(selectedAdmin);
        } catch (error) {
            console.error(error);
            message.error('Erreur lors de la mise à jour de la configuration');
        } finally {
            setIsLoading(false);
        }
    }

    return <Modal
        title="Copier la configuration vers..."
        open={open}
        onCancel={onCancel}
        cancelText="Annuler"
        okText="Copier"
        onOk={handleValidate}
        confirmLoading={isLoading}
    >
        <Flex vertical gap={16}>
            <Alert type='warning' message='Attention' description='Cette action est destructive et ne peut pas être annulée' />

            <DebouncedAdminSelect
                auth={authContext.authentication}
                value={selectedAdmin}
                onChange={(value) => {
                    console.log(value);
                    setSelectedAdmin(value as IAdmin["id"])
                }}
                disabled={isLoading}
            />
        </Flex>
    </Modal>;
}

export default AdminCopyModal;